import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Lesson1Inertia } from "@/components/lessons/Lesson1Inertia";
import { Lesson2FMA } from "@/components/lessons/Lesson2FMA";
import { Lesson3ActionReaction } from "@/components/lessons/Lesson3ActionReaction";
import { Quiz } from "@/components/Quiz";
import {
	ChevronLeft,
	ChevronRight,
	BookOpen,
	Atom,
	FlaskConical,
	ArrowLeftRight,
	Brain,
	GraduationCap,
} from "lucide-react";
import "./index.css";

type Section = "intro" | "law1" | "law2" | "law3" | "quiz";

const SECTIONS: { id: Section; label: string; icon: React.ReactNode; short: string }[] = [
	{
		id: "intro",
		label: "Introduction",
		icon: <BookOpen className="w-4 h-4" />,
		short: "Intro",
	},
	{
		id: "law1",
		label: "Law 1: Inertia",
		icon: <Atom className="w-4 h-4" />,
		short: "Law 1",
	},
	{
		id: "law2",
		label: "Law 2: F = ma",
		icon: <FlaskConical className="w-4 h-4" />,
		short: "Law 2",
	},
	{
		id: "law3",
		label: "Law 3: Action-Reaction",
		icon: <ArrowLeftRight className="w-4 h-4" />,
		short: "Law 3",
	},
	{
		id: "quiz",
		label: "Quiz",
		icon: <Brain className="w-4 h-4" />,
		short: "Quiz",
	},
];

function Introduction({ onStart }: { onStart: () => void }) {
	return (
		<div className="space-y-6">
			<div className="text-center space-y-4 py-6">
				<div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 border border-primary/20">
					<GraduationCap className="w-10 h-10 text-primary" />
				</div>
				<h1 className="text-3xl font-bold tracking-tight">
					Newton's Laws of Motion
				</h1>
				<p className="text-lg text-muted-foreground max-w-xl mx-auto">
					An interactive course exploring the fundamental principles
					that govern how objects move. Investigate net forces,
					inertia, and the relationship between force, friction, mass,
					and acceleration.
				</p>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<Card className="bg-blue-500/5 border-blue-500/20 hover:border-blue-500/40 transition-colors">
					<CardContent className="pt-5 space-y-2">
						<div className="flex items-center gap-2">
							<Atom className="w-5 h-5 text-blue-400" />
							<h3 className="font-semibold">
								First Law: Inertia
							</h3>
						</div>
						<p className="text-sm text-muted-foreground">
							Objects resist changes to their motion. Explore how
							friction affects a moving object's tendency to keep
							going.
						</p>
					</CardContent>
				</Card>

				<Card className="bg-green-500/5 border-green-500/20 hover:border-green-500/40 transition-colors">
					<CardContent className="pt-5 space-y-2">
						<div className="flex items-center gap-2">
							<FlaskConical className="w-5 h-5 text-green-400" />
							<h3 className="font-semibold">
								Second Law: F = ma
							</h3>
						</div>
						<p className="text-sm text-muted-foreground">
							The fundamental equation of mechanics. Control
							force, mass, and friction to see how acceleration
							responds.
						</p>
					</CardContent>
				</Card>

				<Card className="bg-orange-500/5 border-orange-500/20 hover:border-orange-500/40 transition-colors">
					<CardContent className="pt-5 space-y-2">
						<div className="flex items-center gap-2">
							<ArrowLeftRight className="w-5 h-5 text-orange-400" />
							<h3 className="font-semibold">
								Third Law: Action-Reaction
							</h3>
						</div>
						<p className="text-sm text-muted-foreground">
							Forces always come in pairs. Watch collisions and
							see how equal forces produce different accelerations.
						</p>
					</CardContent>
				</Card>
			</div>

			<Card className="bg-card/50 border-muted">
				<CardContent className="pt-5 space-y-3">
					<h3 className="font-semibold">What you'll learn</h3>
					<ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
						<li className="flex items-center gap-2">
							<div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
							How inertia keeps objects in their current state
						</li>
						<li className="flex items-center gap-2">
							<div className="w-1.5 h-1.5 rounded-full bg-green-400" />
							How net force determines acceleration
						</li>
						<li className="flex items-center gap-2">
							<div className="w-1.5 h-1.5 rounded-full bg-red-400" />
							How friction opposes applied forces
						</li>
						<li className="flex items-center gap-2">
							<div className="w-1.5 h-1.5 rounded-full bg-orange-400" />
							How mass affects an object's response to force
						</li>
						<li className="flex items-center gap-2">
							<div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
							How action-reaction pairs work in collisions
						</li>
						<li className="flex items-center gap-2">
							<div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
							How all three laws connect to each other
						</li>
					</ul>
				</CardContent>
			</Card>

			<div className="text-center">
				<Button
					onClick={onStart}
					size="lg"
					className="text-lg px-8 py-6"
				>
					Start Learning
					<ChevronRight className="w-5 h-5 ml-2" />
				</Button>
			</div>
		</div>
	);
}

export function App() {
	const [activeSection, setActiveSection] = useState<Section>("intro");
	const currentIndex = SECTIONS.findIndex((s) => s.id === activeSection);
	const progress = ((currentIndex) / (SECTIONS.length - 1)) * 100;

	const goNext = () => {
		if (currentIndex < SECTIONS.length - 1) {
			setActiveSection(SECTIONS[currentIndex + 1].id);
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	};

	const goPrev = () => {
		if (currentIndex > 0) {
			setActiveSection(SECTIONS[currentIndex - 1].id);
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	};

	return (
		<TooltipProvider>
			<div className="min-h-screen bg-background">
				{/* Top navigation bar */}
				<header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
					<div className="max-w-4xl mx-auto px-4 py-3">
						<div className="flex items-center justify-between gap-4">
							<div className="flex items-center gap-2">
								<GraduationCap className="w-5 h-5 text-primary" />
								<span className="font-semibold text-sm hidden sm:inline">
									Newton's Laws
								</span>
							</div>

							<nav className="flex items-center gap-1">
								{SECTIONS.map((section, i) => (
									<button
										key={section.id}
										onClick={() => setActiveSection(section.id)}
										className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
											activeSection === section.id
												? "bg-primary text-primary-foreground"
												: "text-muted-foreground hover:text-foreground hover:bg-muted"
										}`}
									>
										{section.icon}
										<span className="hidden md:inline">
											{section.short}
										</span>
									</button>
								))}
							</nav>

							<Badge variant="outline" className="text-xs font-mono">
								{currentIndex + 1}/{SECTIONS.length}
							</Badge>
						</div>
						<Progress value={progress} className="mt-2 h-1" />
					</div>
				</header>

				{/* Main content area */}
				<main className="max-w-4xl mx-auto px-4 py-8">
					{activeSection === "intro" && (
						<Introduction onStart={goNext} />
					)}
					{activeSection === "law1" && <Lesson1Inertia />}
					{activeSection === "law2" && <Lesson2FMA />}
					{activeSection === "law3" && <Lesson3ActionReaction />}
					{activeSection === "quiz" && <Quiz />}

					{/* Navigation footer */}
					<Separator className="mt-10 mb-6" />
					<div className="flex items-center justify-between">
						<Button
							onClick={goPrev}
							variant="outline"
							disabled={currentIndex === 0}
							className="flex items-center gap-2"
						>
							<ChevronLeft className="w-4 h-4" />
							{currentIndex > 0
								? SECTIONS[currentIndex - 1].label
								: "Back"}
						</Button>
						<Button
							onClick={goNext}
							disabled={currentIndex === SECTIONS.length - 1}
							className="flex items-center gap-2"
						>
							{currentIndex < SECTIONS.length - 1
								? SECTIONS[currentIndex + 1].label
								: "Finish"}
							<ChevronRight className="w-4 h-4" />
						</Button>
					</div>
				</main>
			</div>
		</TooltipProvider>
	);
}

export default App;
